import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { BAD_EMAIL, REQUIRED, SUBMIT_FAIL, apiFieldErrors, blank, scrollToFirstError, submitErrorMessage } from '@/lib/formErrors';

import { ComboBox } from '@/components/ComboBox';
import { useToast } from '@/components/Toast';
import {
  useClient, useUpdateClient, useLookup, useClientSearch, type ClientPickerRow,
} from '@/api/hooks';
import { api } from '@/api/client';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const US_CA = ['United States', 'Canada'];
// State/province lists per country (verbatim from Power Apps ComboBox_State), same as New Client.
const US_STATES = ['AK','AL','AR','AZ','CA','CO','CT','DC','DE','FL','GA','HI','IA','ID','IL','IN','KS','KY','LA','MA','MD','ME','MI','MN','MO','MS','MT','NC','ND','NE','NH','NJ','NM','NV','NY','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VA','VT','WA','WI','WV','WY'];
const CA_PROVINCES = ['AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QC','SK','YT'];

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const EIN1_RE = /^\d{3}-\d{2}-\d{4}$|^\d{2}-\d{7}$/;
const EIN2_RE = /^\d{3}-\d{2}-\d{4}$/;

// state key -> record column, for initializing the edit form from the client record.
const INIT_MAP: Record<string, string> = {
  clientName: 'ClientName', clientEmail: 'ClientEmail', secondaryEmail: 'SecondaryEmail',
  contactName: 'ContactName', mailingAddress1: 'MailingAddress1', mailingAddress2: 'MailingAddress2',
  city: 'City', state: 'State', zipCode: 'ZipCode', phone: 'Phone', secondaryPhone: 'SecondaryPhone',
  taxSSN1: 'TaxSSN1', taxSSN2: 'TaxSSN2', referralSource: 'ReferralSource',
  surveyEmail: 'SurveyEmail', surveyAttention: 'SurveyAttention',
  clientManager: 'ClientManager', entityType: 'EntityType', brcIndustry: 'BRCIndustry',
  naics: 'NAICS', fiscalYearEndMonth: 'FiscalYearEndMonth', country: 'Country',
  billingStatementEmail: 'BillingStatementEmail',
};

export function ClientEdit() {
  const { clientId, subId } = useParams();
  const navigate = useNavigate();
  const notify = useToast();
  const rec = useClient(clientId, subId);
  const r = rec.data;
  const update = useUpdateClient(clientId ?? '', subId ?? '');
  const [editMode, setEditMode] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [f, setF] = useState<Record<string, string>>({});
  const set = (k: string, v: string) => setF((s) => ({ ...s, [k]: v }));

  // Billing Client ID cascade (only when Bill To = Other) + survey toggle, mirrors New Client.
  const [billingQ, setBillingQ] = useState('');
  const [selectedBilling, setSelectedBilling] = useState<ClientPickerRow | null>(null);
  const [survey, setSurvey] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [apiErrs, setApiErrs] = useState<Record<string, string>>({});

  const managers = useLookup('client-managers');
  const industries = useLookup('brc-industries');
  const naics = useLookup('naics');
  const entityTypes = useLookup('entity-types');
  const countries = useLookup('countries');
  const billingPicker = useClientSearch(f.billingClientType === 'Other' ? billingQ : '', true);
  // View-only Engagement Location, derived from the selected Client Manager.
  const engLocation = useQuery({
    queryKey: ['eng-location', f.clientManager ?? ''],
    queryFn: () => api.get<{ EngLocation: string }[]>(`/lookups/engagement-locations?partner=${encodeURIComponent(f.clientManager ?? '')}`),
    enabled: !!f.clientManager,
  });

  useEffect(() => {
    if (!r) return;
    const init: Record<string, string> = {};
    // Maconomy stores a blank text field as a single space: load those as empty.
    for (const [sk, rk] of Object.entries(INIT_MAP)) { const v = String(r[rk] ?? ''); init[sk] = v.trim() ? v : ''; }
    init.billingClientType = String(r.BillingClientType ?? 'Self');
    init.billingClientId = String(r.BillingClientID ?? '');
    // Show the existing note (Power App binds Note to selectedClientItem.Note).
    // In edit mode it stays editable, pre-filled — matching the Power App's Default.
    init.note = String(r.Note ?? '');
    setF(init);
    setSurvey(!!String(r.SurveyEmail ?? '').trim());
  }, [r]);

  if (rec.isFetching && !r) return <div className="p-6">Loading…</div>;
  if (!r) return <div className="p-6">Client not found.</div>;

  const opType = r.CompletionDate ? 'Complete' : 'Pending';
  const clientType = String(r.ClientType ?? 'Master');
  const isMaster = String(r.ClientSubID ?? '0') === '0'; // master sub id is 0
  const billOptions = clientType === 'Master' ? ['Self', 'Other'] : ['Self', 'Master', 'Other'];
  const country = f.country ?? '';
  const needsState = US_CA.includes(country);
  const stateItems = country === 'United States' ? US_STATES : country === 'Canada' ? CA_PROVINCES : [];

  const badEmail = (v?: string) => !blank(v) && !EMAIL_RE.test(String(v).trim());
  const clientEmailBad = badEmail(f.clientEmail);
  const secEmailBad = badEmail(f.secondaryEmail);
  const surveyEmailBad = badEmail(f.surveyEmail);
  const billStmtEmailBad = badEmail(f.billingStatementEmail);
  const ein1Bad = !!f.taxSSN1 && !EIN1_RE.test(f.taxSSN1);
  const ein2Bad = !!f.taxSSN2 && !EIN2_RE.test(f.taxSSN2);


  // Item: highlight the fields blocking a submit (shown after the first Submit attempt).
  const errs: Record<string, string> = {};
  {
    const v = f as Record<string, string | undefined>;
    for (const k of ['clientName', 'contactName', 'mailingAddress1', 'city', 'zipCode', 'phone', 'clientEmail', 'billingStatementEmail']) {
      if (blank(v[k])) errs[k] = REQUIRED;
    }
    for (const k of ['clientManager', 'entityType', 'brcIndustry', 'naics', 'fiscalYearEndMonth']) if (blank(v[k])) errs[k] = REQUIRED;
    if (blank(country)) errs.country = REQUIRED;
    if (needsState && blank(v.state)) errs.state = REQUIRED;
    if (!errs.clientEmail && clientEmailBad) errs.clientEmail = BAD_EMAIL;
    if (secEmailBad) errs.secondaryEmail = BAD_EMAIL;
    if (surveyEmailBad) errs.surveyEmail = BAD_EMAIL;
    if (!errs.billingStatementEmail && billStmtEmailBad) errs.billingStatementEmail = BAD_EMAIL;
    if (ein1Bad) errs.taxSSN1 = 'Format: XX-XXXXXXX or XXX-XX-XXXX';
    if (ein2Bad) errs.taxSSN2 = 'Format: XXX-XX-XXXX';
    if (blank(f.note)) errs.note = REQUIRED;
    if (!f.billingClientType) errs.billTo = REQUIRED;
    if (f.billingClientType === 'Other' && blank(f.billingClientId)) errs.billingClient = REQUIRED;
  }
  const E: Record<string, string | undefined> = showErrors ? { ...apiErrs, ...errs } : apiErrs;

  function changeCountry(v: string) {
    setF((s) => ({ ...s, country: v, state: '' })); // Country OnChange → Reset(State)
  }

  async function save() {
    setConfirm(false);
    const body = { ...f, clientId, clientSubId: subId, clientType };
    try {
      await update.mutateAsync({ body, clientType, opType });
      notify('success', 'Client change submitted (new pending request created).');
      navigate('/clients');
    } catch (e) {
      setApiErrs(apiFieldErrors(e));
      setShowErrors(true);
      notify('error', submitErrorMessage(e));
      scrollToFirstError();
    }
  }

  /** Submit: highlight what's missing before asking for confirmation. */
  function attempt() {
    setApiErrs({});
    if (Object.keys(errs).length) {
      setShowErrors(true);
      notify('error', SUBMIT_FAIL);
      scrollToFirstError();
      return;
    }
    setConfirm(true);
  }

  const D = !editMode; // disabled shorthand

  return (
    <div className="w-full space-y-4 p-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">{String(r.ClientName || `Client ${r.ClientID}`)}</h2>
          <p className="text-xs text-muted-foreground">
            {isMaster
              ? `Master Client · ID ${String(r.ClientID)}`
              : `Sub Client · ID ${String(r.ClientID)} / Sub ${String(r.ClientSubID)}`}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to={`/clients/${clientId}/${subId}/additional`} className="btn-secondary">Additional Details</Link>
          {!editMode ? <button className="btn-primary" onClick={() => setEditMode(true)}>Edit Details</button> : (
            <>
              <button className="btn-secondary" onClick={() => { setEditMode(false); rec.refetch(); }}>Cancel</button>
              <button className="btn-success" disabled={update.isPending} onClick={attempt}>Submit</button>
            </>
          )}
        </div>
      </div>
      {editMode && <div className="rounded-md border border-warning/40 bg-warning/10 p-2 text-xs">Submitting creates a <b>new Pending Request</b>. <b>Note is required.</b></div>}

      <div className="grid gap-4 xl:grid-cols-2 xl:items-start">
        <Section title="Client Information">
          <Field label="Client ID"><input className="input" value={String(r.ClientID)} disabled /></Field>
          <Field label="Client Sub ID"><input className="input" value={String(r.ClientSubID)} disabled /></Field>
          <Field label="Client Type"><input className="input" value={clientType} disabled /></Field>

          <Field error={E.clientName} label="Client Name *">
            <input className="input" disabled={D} value={f.clientName ?? ''} onChange={(e) => set('clientName', e.target.value)} />
            <p className="mt-1 text-xs text-muted-foreground">If client is an individual, use the &quot;Last Name, First Name&quot; format.</p>
          </Field>
          <Field error={E.clientEmail} label={<>Client Email * <Warn show={clientEmailBad} tip="Enter a valid email" /></>}>
            <input className="input" disabled={D} value={f.clientEmail ?? ''} onChange={(e) => set('clientEmail', e.target.value)} />
          </Field>
          <Field error={E.secondaryEmail} label={<>Secondary Email <Warn show={secEmailBad} tip="Enter a valid email" /></>}>
            <input className="input" disabled={D} value={f.secondaryEmail ?? ''} onChange={(e) => set('secondaryEmail', e.target.value)} />
          </Field>

          <Field error={E.billTo} label="Bill To *">
            <select className="input" disabled={D} value={f.billingClientType ?? ''}
              onChange={(e) => { set('billingClientType', e.target.value); setSelectedBilling(null); }}>
              <option value="">— select —</option>
              {billOptions.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
          <Field error={E.billingClient} label={`Billing Client ID ${f.billingClientType === 'Other' ? '*' : ''}`}>
            {editMode && f.billingClientType === 'Other' ? (
              <ComboBox<ClientPickerRow>
                items={billingPicker.data ?? []}
                value={selectedBilling}
                onChange={(c) => { setSelectedBilling(c); set('billingClientId', c?.ClientID ?? ''); }}
                getLabel={(c) => c.ClientFullName ?? `${c.ClientID}-${c.ClientName}`}
                getKey={(c) => c.ClientFullName ?? `${c.ClientID}-${c.ClientName}`}
                placeholder={f.billingClientId ? `Current: ${f.billingClientId}` : 'Type to search by ID or name…'}
                loading={billingPicker.isFetching}
                onQueryChange={setBillingQ}
              />
            ) : (
              // View mode, or Bill To = Self/Master (derived, not user-picked):
              // show the stored Billing Client ID as a readable value.
              <input className="input" disabled value={f.billingClientId ?? ''} />
            )}
          </Field>
          <Field error={E.billingStatementEmail} label={<>Billing Statement Email * <Warn show={billStmtEmailBad} tip="Enter a valid email" /></>}>
            <input className="input" disabled={D} value={f.billingStatementEmail ?? ''} onChange={(e) => set('billingStatementEmail', e.target.value)} />
          </Field>
        </Section>

        <Section title="Contact Details">
          <Field error={E.contactName} label="Contact Name *"><input className="input" disabled={D} value={f.contactName ?? ''} onChange={(e) => set('contactName', e.target.value)} /></Field>
          <Field error={E.mailingAddress1} label="Mailing Address 1 *"><input className="input" disabled={D} value={f.mailingAddress1 ?? ''} onChange={(e) => set('mailingAddress1', e.target.value)} /></Field>
          <Field label="Mailing Address 2"><input className="input" disabled={D} value={f.mailingAddress2 ?? ''} onChange={(e) => set('mailingAddress2', e.target.value)} /></Field>
          <Field error={E.city} label="City *"><input className="input" disabled={D} value={f.city ?? ''} onChange={(e) => set('city', e.target.value)} /></Field>
          <Field error={E.country} label="Country *">
            <LookupCombo data={countries.data} field="Value" value={country} disabled={D}
              onChange={changeCountry} placeholder="Select country…" />
          </Field>
          <Field error={E.state} label={`State/Province ${needsState ? '*' : ''}`}>
            <ComboBox<string>
              items={stateItems}
              value={f.state ? f.state : null}
              onChange={(s) => set('state', s ?? '')}
              getLabel={(s) => s}
              getKey={(s) => s}
              placeholder="Select state…"
              disabled={D || !needsState}
            />
          </Field>
          <Field error={E.zipCode} label="Zip/Postal Code *"><input className="input" disabled={D} value={f.zipCode ?? ''} onChange={(e) => set('zipCode', e.target.value)} /></Field>
          <Field error={E.phone} label="Phone *"><input className="input" disabled={D} value={f.phone ?? ''} onChange={(e) => set('phone', e.target.value)} /></Field>
          <Field label="Secondary Phone"><input className="input" disabled={D} value={f.secondaryPhone ?? ''} onChange={(e) => set('secondaryPhone', e.target.value)} /></Field>
        </Section>

        <Section title="Other Details">
          <Field error={E.clientManager} label="Client Manager *">
            <LookupCombo data={managers.data} field="ClientManager" value={f.clientManager ?? ''} disabled={D}
              onChange={(v) => set('clientManager', v)} placeholder="Select manager…" />
          </Field>
          <Field label="Engagement Location">
            <input className="input" value={String(engLocation.data?.[0]?.EngLocation ?? '')} disabled placeholder="Set by Client Manager" />
          </Field>
          <Field error={E.entityType} label="Entity Type *">
            <LookupCombo data={entityTypes.data} field="EntityType" labelField="Description" value={f.entityType ?? ''} disabled={D}
              onChange={(v) => set('entityType', v)} placeholder="Select entity type…" />
          </Field>
          <Field error={E.brcIndustry} label="BRC Industry *">
            <LookupCombo data={industries.data} field="Industry" value={f.brcIndustry ?? ''} disabled={D}
              onChange={(v) => set('brcIndustry', v)} placeholder="Select industry…" />
          </Field>
          <Field error={E.naics} label="NAICS *">
            <LookupCombo data={naics.data} field="NAICS" value={f.naics ?? ''} disabled={D}
              onChange={(v) => set('naics', v)} placeholder="Search NAICS…" />
          </Field>
          <Field error={E.fiscalYearEndMonth} label="Fiscal Year End Month *">
            <select className="input" disabled={D} value={f.fiscalYearEndMonth ?? ''} onChange={(e) => set('fiscalYearEndMonth', e.target.value)}>
              <option value="">— select —</option>
              {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
          <Field error={E.taxSSN1} label={<>Tax EIN/Taxpayer SSN <Warn show={ein1Bad} tip="Format: XX-XXXXXXX or XXX-XX-XXXX" /></>}>
            <input className="input" maxLength={11} placeholder="00-0000000" disabled={D} value={f.taxSSN1 ?? ''} onChange={(e) => set('taxSSN1', e.target.value)} />
          </Field>
          <Field error={E.taxSSN2} label={<>Spouse SSN <Warn show={ein2Bad} tip="Format: XXX-XX-XXXX" /></>}>
            <input className="input" maxLength={11} placeholder="000-00-0000" disabled={D} value={f.taxSSN2 ?? ''} onChange={(e) => set('taxSSN2', e.target.value)} />
          </Field>
          <Field label="Referral Source"><input className="input" disabled={D} value={f.referralSource ?? ''} onChange={(e) => set('referralSource', e.target.value)} /></Field>
        </Section>

        <Section title="Survey">
          <div className="col-span-full flex items-center gap-2">
            <input id="surveyChk" type="checkbox" disabled={D} checked={survey}
              onChange={(e) => {
                setSurvey(e.target.checked);
                if (!e.target.checked) setF((s) => ({ ...s, surveyEmail: '', surveyAttention: '' }));
              }} />
            <label htmlFor="surveyChk" className="text-sm font-semibold text-foreground">Survey - Check if different from contact above</label>
          </div>
          <Field error={E.surveyEmail} label={<>Survey Email <Warn show={surveyEmailBad} tip="Enter a valid email" /></>}>
            <input className="input" disabled={D || !survey} value={f.surveyEmail ?? ''} onChange={(e) => set('surveyEmail', e.target.value)} />
          </Field>
          <Field label="Survey Attention">
            <input className="input" disabled={D || !survey} value={f.surveyAttention ?? ''} onChange={(e) => set('surveyAttention', e.target.value)} />
          </Field>
        </Section>
      </div>

      <div>
        <div data-field-error={E.note ? 'true' : undefined} className={E.note ? 'field-error' : undefined}>
          <label className="label">Note {editMode ? '*' : ''}</label>
          <textarea className="input" rows={3} disabled={D} value={f.note ?? ''} onChange={(e) => set('note', e.target.value)} />
          {E.note && <p className="mt-1 text-xs font-medium text-destructive">{E.note}</p>}
        </div>
      </div>

      {editMode && <p className="text-xs font-semibold text-accent">*  Mandatory Fields</p>}

      <ConfirmDialog open={confirm}
        message="Do you wish to submit the client form changes? Press 'Yes' to continue, 'No' to cancel."
        onConfirm={save} onCancel={() => setConfirm(false)} />
    </div>
  );
}

/** ComboBox bound to a lookup table by a single display column (stores the string). */
function LookupCombo({ data, field, labelField, value, onChange, placeholder, disabled }: {
  data?: Record<string, unknown>[];
  field: string;
  labelField?: string; // shown in the list when it differs from the saved field (Entity Type)
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const rows = Array.isArray(data) ? data : [];
  const selected = rows.find((r) => String(r[field] ?? '') === value) ?? null;
  return (
    <ComboBox
      items={rows}
      value={selected}
      onChange={(r) => onChange(r ? String(r[field] ?? '') : '')}
      getLabel={(r) => String(r[labelField ?? field] ?? '')}
      getKey={(r) => String(r[field] ?? '')}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
}

function Warn({ show, tip }: { show: boolean; tip: string }) {
  if (!show) return null;
  return <span title={tip} className="ml-1 font-bold text-orange-500">!</span>;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <h3 className="mb-2 border-b border-border pb-2 text-sm font-semibold uppercase tracking-wide text-foreground">{title}</h3>
      <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">{children}</div>
    </div>
  );
}

function Field({ label, children, error }: { label: React.ReactNode; children: React.ReactNode; error?: string }) {
  // error: red border on the input + message underneath (fields blocking a submit).
  return (
    <div data-field-error={error ? 'true' : undefined} className={error ? 'field-error' : undefined}>
      <label className="label">{label}</label>
      {children}
      {error && <p className="mt-1 text-xs font-medium text-destructive">{error}</p>}
    </div>
  );
}
